import { BigNumber, ethers } from 'ethers';
import { useCallback, useMemo } from 'react';
import { useHasPendingApproval, useTransactionAdder } from '../state/transactions/hooks';
import useAllowance from './useAllowance';
import ERC20 from '../tomb-finance/ERC20';
import { ETH_TICKER, HOG_TICKER, GHOG_TICKER } from '../utils/constants';
import useTombFinance from './useTombFinance';

export enum ApprovalState {
  UNKNOWN,
  NOT_APPROVED,
  PENDING,
  APPROVED,
}

// Add these addresses from the deployments
const HOG_OS_ZAPPER_ADDR = "0x3651f641BCD424077c1AcD7D469B92E40E0C385d";
const GHOG_OS_ZAPPER_ADDR = "0x6bA5cfd5dFcdEbc77D316f0E87194f046A838243";

function useApproveZapper(
  zappingToken: string, 
  lpName: string,
  direction: 'in' | 'out'
): [ApprovalState, (amount: string) => Promise<void>, BigNumber | null] {
  const tombFinance = useTombFinance();
  const addTransaction = useTransactionAdder();

  // Determine which zapper address to use based on the LP name
  const zapperAddress = useMemo(() => {
    if (lpName === 'HOG-OS-LP') {
      return HOG_OS_ZAPPER_ADDR;
    } else if (lpName === 'GHOG-OS-LP') {
      return GHOG_OS_ZAPPER_ADDR;
    }
    return null;
  }, [lpName]);

  // Get token instance based on direction
  const token = useMemo(() => {
    if (direction === 'out') {
      // For zap out, always use the LP token
      if (lpName === 'HOG-OS-LP') {
        return tombFinance.HOG_OS_LP;
      } else if (lpName === 'GHOG-OS-LP') {
        return tombFinance.GHOG_OS_LP;
      }
    } else {
      // For zap in, use the input token
      if (zappingToken === ETH_TICKER) return tombFinance.S;
      if (zappingToken === HOG_TICKER) return tombFinance.HOG;
      if (zappingToken === GHOG_TICKER) return tombFinance.GHOG;
      if (zappingToken === 'OS') return tombFinance.OS;
    }
    return null;
  }, [direction, lpName, zappingToken, tombFinance]);

  // Always call hooks, but use null checks in the logic
  const pendingApproval = useHasPendingApproval(token?.address || ethers.constants.AddressZero, zapperAddress || ethers.constants.AddressZero);
  const currentAllowance = useAllowance(token, zapperAddress || '', pendingApproval);

  // check the current approval status
  const approvalState: ApprovalState = useMemo(() => {
    if (!token || !zapperAddress) return ApprovalState.UNKNOWN;
    if (token === tombFinance.S) return ApprovalState.APPROVED;
    if (!currentAllowance) return ApprovalState.UNKNOWN;

    // If there's any allowance at all, consider it initially approved
    // Individual transactions will check for sufficient allowance
    return currentAllowance.gt(0)
      ? ApprovalState.APPROVED
      : pendingApproval
        ? ApprovalState.PENDING
        : ApprovalState.NOT_APPROVED;
  }, [currentAllowance, pendingApproval, token, tombFinance, zapperAddress]);

  const approve = useCallback(async (amount: string): Promise<void> => {
    if (!token || !zapperAddress) {
      console.error('No token or zapper address to approve');
      return;
    }

    try {
      const bigAmount = ethers.utils.parseUnits(amount);
      // Create a new contract instance with the signer
      const tokenContract = new ethers.Contract(
        token.address,
        ['function approve(address spender, uint256 amount) returns (bool)'],
        tombFinance.signer
      );
      
      const response = await tokenContract.approve(zapperAddress, bigAmount);
      addTransaction(response, {
        summary: `Approve ${token.symbol}`,
        approval: {
          tokenAddress: token.address,
          spender: zapperAddress,
        },
      });
    } catch (error) {
      console.error('Error approving token:', error);
    }
  }, [token, zapperAddress, addTransaction, tombFinance.signer]);

  return [approvalState, approve, currentAllowance];
}

export default useApproveZapper;
